import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useGisStore } from '@/stores/useGisStore.js';
import { DEFAULT_VEHICLE_LAT, DEFAULT_VEHICLE_LON } from '@k-ucap/shared';
import type { VehicleState } from '@k-ucap/shared';
import type { TrackPoint, GisAlert } from '@/domain/gis/GisMessageProjectionService.js';

const PADDING = 0.05; // degrees padding
const MIN_VIEW = 0.02; // minimum view span in degrees

interface Point2D {
  x: number;
  y: number;
}

const toSvgPoint = (
  lat: number,
  lon: number,
  minLat: number,
  maxLat: number,
  minLon: number,
  maxLon: number,
  width: number,
  height: number
): Point2D => {
  const latRange = maxLat - minLat || MIN_VIEW;
  const lonRange = maxLon - minLon || MIN_VIEW;
  const x = ((lon - minLon) / lonRange) * width;
  const y = height - ((lat - minLat) / latRange) * height;
  return { x, y };
};

const degToRad = (deg: number) => (deg * Math.PI) / 180;

const statusFill = (status: string): string => {
  const s = status.toUpperCase();
  if (s.includes('CRITICAL') || s.includes('FAIL') || s.includes('DANGER')) return '#ef4444';
  if (s.includes('WARN')) return '#f59e0b';
  if (s.includes('CRUISE') || s.includes('NORMAL') || s.includes('INERTIAL')) return '#22c55e';
  return '#3b82f6';
};

const severityFill = (severity: GisAlert['severity']): string => {
  if (severity === 'CRITICAL') return '#ef4444';
  if (severity === 'WARNING') return '#f59e0b';
  if (severity === 'INFO') return '#3b82f6';
  return '#9ca3af';
};

export const FallbackMap = () => {
  const vehicles = useGisStore((s) => s.vehicles);
  const tracks = useGisStore((s) => s.tracks);
  const routes = useGisStore((s) => s.routes);
  const alerts = useGisStore((s) => s.alerts);
  const selectedVehicleId = useGisStore((s) => s.selectedVehicleId);
  const showTracks = useGisStore((s) => s.showTracks);
  const showRoutes = useGisStore((s) => s.showRoutes);
  const selectVehicle = useGisStore((s) => s.selectVehicle);

  const vehicleList = useMemo(() => Object.values(vehicles), [vehicles]);
  const routeList = useMemo(() => Object.values(routes), [routes]);
  const activeAlerts = useMemo(() => alerts.filter((a) => !a.acknowledged), [alerts]);

  const bounds = useMemo(() => {
    const lats: number[] = [DEFAULT_VEHICLE_LAT];
    const lons: number[] = [DEFAULT_VEHICLE_LON];
    vehicleList.forEach((v) => {
      lats.push(v.coordinates.latitude);
      lons.push(v.coordinates.longitude);
    });
    routeList.forEach((r) =>
      r.waypoints.forEach((w) => {
        lats.push(w.coordinates.latitude);
        lons.push(w.coordinates.longitude);
      })
    );
    activeAlerts.forEach((a) => {
      if (a.coordinates) {
        lats.push(a.coordinates.latitude);
        lons.push(a.coordinates.longitude);
      }
    });
    return {
      minLat: Math.min(...lats) - PADDING,
      maxLat: Math.max(...lats) + PADDING,
      minLon: Math.min(...lons) - PADDING,
      maxLon: Math.max(...lons) + PADDING,
    };
  }, [vehicleList, routeList, activeAlerts]);

  const width = 800;
  const height = 600;

  const project = (lat: number, lon: number) =>
    toSvgPoint(lat, lon, bounds.minLat, bounds.maxLat, bounds.minLon, bounds.maxLon, width, height);

  const renderTrack = (vehicleId: string, points: TrackPoint[]) => {
    if (!showTracks || points.length < 2) return null;
    const d = points
      .map((p, i) => {
        const { x, y } = project(p.latitude, p.longitude);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');
    return <path key={`track-${vehicleId}`} d={d} fill="none" stroke="#3b82f6" strokeWidth={1.5} opacity={0.8} />;
  };

  const renderVehicle = (vehicle: VehicleState) => {
    const { x, y } = project(vehicle.coordinates.latitude, vehicle.coordinates.longitude);
    const heading = vehicle.headingDeg ?? 0;
    const size = 10;
    const rad = degToRad(heading - 90);
    const p1 = { x: x + Math.cos(rad) * size, y: y + Math.sin(rad) * size };
    const p2 = { x: x + Math.cos(rad + 2.5) * size * 0.8, y: y + Math.sin(rad + 2.5) * size * 0.8 };
    const p3 = { x: x + Math.cos(rad - 2.5) * size * 0.8, y: y + Math.sin(rad - 2.5) * size * 0.8 };
    const isSelected = selectedVehicleId === vehicle.vehicleId;
    const color = statusFill(vehicle.status);
    return (
      <g key={vehicle.vehicleId} onClick={() => selectVehicle(vehicle.vehicleId)} style={{ cursor: 'pointer' }}>
        <polygon
          points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`}
          fill={color}
          stroke="white"
          strokeWidth={1}
        />
        {isSelected && (
          <circle cx={x} cy={y} r={size + 4} fill="none" stroke="white" strokeWidth={1} strokeDasharray="2,2" />
        )}
        <text x={x} y={y - size - 4} textAnchor="middle" fill="white" fontSize={10} stroke="black" strokeWidth={0.3}>
          {vehicle.callsign || vehicle.vehicleId}
        </text>
      </g>
    );
  };

  const renderRoute = (routeId: string) => {
    if (!showRoutes) return null;
    const route = routes[routeId];
    if (!route || route.waypoints.length < 2) return null;
    const d = route.waypoints
      .map((w, i) => {
        const { x, y } = project(w.coordinates.latitude, w.coordinates.longitude);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');
    return <path key={`route-${routeId}`} d={d} fill="none" stroke="#22c55e" strokeWidth={2} opacity={0.8} />;
  };

  const renderWaypoint = (waypoint: {
    order: number;
    coordinates: { latitude: number; longitude: number; altitudeM: number };
    name?: string;
  }) => {
    const { x, y } = project(waypoint.coordinates.latitude, waypoint.coordinates.longitude);
    return (
      <g key={`wp-${waypoint.order}`}>
        <circle cx={x} cy={y} r={4} fill="#f59e0b" stroke="white" strokeWidth={1} />
        <text x={x} y={y - 6} textAnchor="middle" fill="white" fontSize={9} stroke="black" strokeWidth={0.3}>
          WP{waypoint.order}
        </text>
      </g>
    );
  };

  const renderAlert = (alert: GisAlert) => {
    const vehicle = vehicles[alert.vehicleId];
    const lat = alert.coordinates?.latitude ?? vehicle?.coordinates.latitude ?? DEFAULT_VEHICLE_LAT;
    const lon = alert.coordinates?.longitude ?? vehicle?.coordinates.longitude ?? DEFAULT_VEHICLE_LON;
    const { x, y } = project(lat, lon);
    const color = severityFill(alert.severity);
    const size = 6;
    return (
      <g key={`alert-${alert.id}`}>
        <polygon
          points={`${x},${y - size} ${x + size},${y} ${x},${y + size} ${x - size},${y}`}
          fill={color}
          stroke="white"
          strokeWidth={1}
        />
        <text x={x} y={y - size - 4} textAnchor="middle" fill={color} fontSize={9} stroke="black" strokeWidth={0.3}>
          {alert.message}
        </text>
      </g>
    );
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}
    >
      <Paper
        sx={{ mx: 1, mt: 1, px: 1, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="subtitle2">2D Situation Panel (Fallback)</Typography>
      </Paper>
      <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          <rect width={width} height={height} fill="#0b1120" />
          <g opacity={0.2}>
            {Array.from({ length: 10 }).map((_, i) => (
              <line
                key={`grid-v-${i}`}
                x1={(i * width) / 10}
                y1={0}
                x2={(i * width) / 10}
                y2={height}
                stroke="#334155"
                strokeWidth={0.5}
              />
            ))}
            {Array.from({ length: 10 }).map((_, i) => (
              <line
                key={`grid-h-${i}`}
                x1={0}
                y1={(i * height) / 10}
                x2={width}
                y2={(i * height) / 10}
                stroke="#334155"
                strokeWidth={0.5}
              />
            ))}
          </g>
          {Object.entries(tracks).map(([vehicleId, points]) => renderTrack(vehicleId, points))}
          {Object.keys(routes).map((routeId) => renderRoute(routeId))}
          {showRoutes && routeList.flatMap((route) => route.waypoints.map((w) => renderWaypoint(w)))}
          {vehicleList.map(renderVehicle)}
          {activeAlerts.map(renderAlert)}
        </svg>
      </Box>
    </Box>
  );
};

export default FallbackMap;
